import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import {stateFromHTML} from 'draft-js-import-html';
import cx from 'classnames';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import '@/scss/components/common/RichTextEditor.scss';

/*
this component differs from others in that it is stateful
which means it doesn't get its value from the parent component's state,
but it still provides the resulting html string to the onChange callback
so its parent can do things like onChange validation and submission
*/
class RichTextEditor extends React.Component {
	constructor(props) {
		super(props);
		this.handleEditorStateChange = this.handleEditorStateChange.bind(this);
		this.handleBlur = this.handleBlur.bind(this);
		this.handleDefaultTextClick = this.handleDefaultTextClick.bind(this);

		let contentState = stateFromHTML(props.initialValue, stateFromHTMLOptions);
		let editorState = EditorState.createWithContent(contentState);
		this.state = {editorState};
	}

	componentDidUpdate() {
		// account for props changing when another a one of mapped set RTE elements gets deleted, update according to props, not according to previous content of RTE at that index location
		if (stateToHTML(stateFromHTML(this.props.initialValue, stateFromHTML)) != stateToHTML(this.state.editorState.getCurrentContent())) {
			let contentState = stateFromHTML(this.props.initialValue, stateFromHTMLOptions);
			let editorState = EditorState.createWithContent(contentState);
			this.setState({editorState});
		}
	}

	handleEditorStateChange(editorState) {
		this.setState({editorState});
		let stateAsHTML = stateToHTML(editorState.getCurrentContent(), stateToHTMLOptions);
		this.props.onChange(stateAsHTML);
	}

	handleBlur() {
		let stateAsHTML = stateToHTML(this.state.editorState.getCurrentContent(), stateToHTMLOptions);
		this.props.onBlur(stateAsHTML);
	}

	handleDefaultTextClick() {
		let contentState = stateFromHTML(this.props.defaultText, stateFromHTMLOptions);
		let editorState = EditorState.createWithContent(contentState);
		this.setState({editorState});
		this.props.defaultTextCallback();
	}

	render() {
		let toolbarOptions = ['inline', 'list', 'link'];
		if (this.props.showColorPicker) {
			toolbarOptions.push('colorPicker');
			toolbarOptions.push('remove');
		}

		// If the user changes block type before entering any text, we can
		// either style the placeholder or hide it. Let's just hide it now.
		let contentState = this.state.editorState.getCurrentContent();
		let className = 'RichTextEditor';
		if (!contentState.hasText()) {
			if (contentState.getBlockMap().first().getType() !== 'unstyled') {
				className += ' hideDraftEditorPlaceholder';
			}
		}

		return (
			<div className={className}>
				<Editor
					editorState={this.state.editorState}
					editorClassName="border rounded px-3"
					toolbar={this.props.toolbar || {
						options: toolbarOptions,
						inline: { options: ['bold', 'italic',] },
						list: { options: ['unordered'] },
						link: { options: ['link', 'unlink'], defaultTargetOption: '_blank' }
					}}
					onEditorStateChange={this.handleEditorStateChange}
					onBlur={this.handleBlur}
					className={cx('form-control', {"is-invalid": this.props.touched && this.props.errorMessage})}
					placeholder={this.props.placeholder}
				/>
				{this.props.touched && this.props.errorMessage &&
					<React.Fragment>
						<div className='form-control is-invalid d-none'></div>
						<div className='invalid-feedback'>
							{this.props.errorMessage}
						</div>
					</React.Fragment>
				}
				{this.props.defaultText &&
					<button className='resetButton' onClick={this.handleDefaultTextClick}>
						Reset to Default
					</button>
				}
			</div>
		);
	}
}

const KEY_TO_CSS = {
	'color': 'color',
	'bgcolor': 'background-color',
};

const CSS_TO_KEY = Object.fromEntries(
	Object
		.entries(KEY_TO_CSS)
		.map(([key, value]) => [value, key])
);

const stateToHTMLOptions = {
	entityStyleFn: (entity) => {
		if (entity.get('type').toLowerCase() === 'link') {
			const data = entity.getData();
			// if the link was created/updated by the WYSIWYG, target attr will be keyed as 'targetOption'
			// if the link was imported to content state it will be data.target
			// we can safely take targetOption over target, as it will always be the fresher of the two
			// be warned though, this code is required because of an issue with npm package draft-js-import/export-html and could change
			return {
				element: 'a',
				attributes: {
					href: data.url,
					target: data.targetOption || data.target
				}
			};
		}
	},
	inlineStyleFn: (styles) => {
		const colorKeys = ['color', 'bgcolor'];

		const outputStyles = {};
		for (let key of colorKeys) {
			const color = styles.filter(value => value.startsWith(key)).first();

			if (!color) continue;

			const outputStyle = KEY_TO_CSS[key];
			outputStyles[outputStyle] = color.replace(`${key}-`, '');
		}

		return {
			element: 'span',
			style: outputStyles,
		};
	}
};

const stateFromHTMLOptions = {
	customInlineFn: (element, {Style, Entity}) => {
		if (element.tagName.toLowerCase() === 'a') {
			// targetOption must be part of entity data for react-wysiwig to render "open in new link" checkbox correctly
			return Entity('LINK', {
				url: element.getAttribute('href'),
				target: element.getAttribute('target'),
				targetOption: element.getAttribute('target')
			});
		}

		if (element.tagName.toLowerCase() === 'span') {
			// check explicitly for color for now
			// could not figure out how to get both color and background-color to get loaded in at the same time
			const rawStyles = element.getAttribute('style');
			if (rawStyles) {
				return rawStyles
					.split(';')
					.map(style => {
						let splitStyle = style.split(':');
						splitStyle[0] = CSS_TO_KEY[splitStyle[0]];
						return splitStyle.join('-');
					})
					.map(style => {
						return Style(style);
					})
					.find(style => style.style.startsWith('color-'));
			}
		}
	}
};

RichTextEditor.propTypes = {
	onChange: PropTypes.func.isRequired,
	onBlur: PropTypes.func.isRequired,
	initialValue: PropTypes.string,
	toolbar: PropTypes.object,
	placeholder: PropTypes.string,
	touched: PropTypes.bool.isRequired,
	errorMessage: PropTypes.string,
	defaultText: PropTypes.string,
	defaultTextCallback: PropTypes.func,
	showColorPicker: PropTypes.bool,
};

export default RichTextEditor;
